import React from 'react';
import { HeartIcon, ZapIcon, CircleIcon, BrainIcon, SparklesIcon, MoonIcon, BarChartIcon } from 'lucide-react';
interface TrackerScreenProps {
  symptoms: Record<string, number>;
  setSymptom: (key: string, value: number) => void;
  showToast: (msg: string) => void;
}
export function TrackerScreen({
  symptoms,
  setSymptom,
  showToast
}: TrackerScreenProps) {
  const symptomList = [{
    id: 'bloat',
    name: 'Bloating',
    icon: HeartIcon,
    color: 'text-red-500'
  }, {
    id: 'energy',
    name: 'Energy',
    icon: ZapIcon,
    color: 'text-yellow-500'
  }, {
    id: 'puff',
    name: 'Puffiness',
    icon: CircleIcon,
    color: 'text-blue-500'
  }, {
    id: 'brain',
    name: 'Brain Fog',
    icon: BrainIcon,
    color: 'text-purple-500'
  }, {
    id: 'skin',
    name: 'Skin Glow',
    icon: SparklesIcon,
    color: 'text-orange-400'
  }, {
    id: 'sleep',
    name: 'Sleep Quality',
    icon: MoonIcon,
    color: 'text-indigo-500'
  }];
  return <div className="flex flex-col min-h-screen pb-24 animate-fade-in" data-id="element-342">
      <div className="bg-gradient-to-br from-[#1A2040] via-[#2A3060] to-[#5060A0] px-5 pt-6 pb-8" data-id="element-343">
        <div className="text-white/70 text-[13px] mb-1" data-id="element-344">Daily Check-In</div>
        <div className="text-white text-2xl font-extrabold mb-1.5" data-id="element-345">
          How Do You Feel?
        </div>
        <div className="text-white/70 text-sm" data-id="element-346">
          Day 3 of 7 · Tap to rate each symptom
        </div>
      </div>

      <div className="px-5 pt-5 pb-2.5 flex items-center justify-between" data-id="element-347">
        <div className="text-lg font-bold text-text-dark" data-id="element-348">Symptom Tracker</div>
        <div className="text-xs text-sage-dark font-semibold" data-id="element-349">
          1 = bad · 5 = great
        </div>
      </div>

      <div className="px-5 grid grid-cols-2 gap-3 mb-2" data-id="element-350">
        {symptomList.map(sym => {
        const val = symptoms[sym.id] || 3;
        const isActive = val >= 4;
        return <div key={sym.id} className={`rounded-2xl p-4 transition-all duration-200 shadow-[0_2px_12px_rgba(0,0,0,0.06)] ${isActive ? 'bg-gradient-to-br from-[#E8F4EA] to-[#D0ECD4] border-2 border-sage-dark' : 'bg-white border-2 border-transparent'}`} data-id="element-351">
              <sym.icon className={`w-7 h-7 mb-2 ${sym.color}`} data-id="element-352" />
              <div className="text-[13px] font-semibold text-text-dark mb-1" data-id="element-353">
                {sym.name}
              </div>
              <div className="flex gap-2" data-id="element-354">
                {[1, 2, 3, 4, 5].map(i => <button key={i} type="button" onClick={() => setSymptom(sym.id, i)} className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-200 active:scale-90 ${i <= val ? 'bg-sage-dark text-white' : 'bg-[#F0F0F0] text-text-light'}`} data-id="element-355">
                    {i}
                  </button>)}
              </div>
            </div>;
      })}
      </div>

      <div className="px-5 pt-3 pb-5" data-id="element-356">
        <div className="text-base font-bold text-text-dark mb-3 flex items-center gap-2" data-id="element-357">
          <BarChartIcon className="w-5 h-5 text-sage-dark" data-id="element-358" /> Your Progress
          Overview
        </div>
        <div className="bg-white rounded-[20px] p-5 shadow-[0_2px_12px_rgba(0,0,0,0.06)]" data-id="element-359">
          {[{
          label: 'Bloating',
          pct: '60%',
          bg: 'from-[#7A9E7E] to-[#4A7A50]',
          val: 'Better'
        }, {
          label: 'Energy',
          pct: '75%',
          bg: 'from-[#C9A84C] to-[#E8A000]',
          val: 'Good ↑'
        }, {
          label: 'Puffiness',
          pct: '50%',
          bg: 'from-[#5BA4A4] to-[#2A7A7A]',
          val: 'Day 3'
        }, {
          label: 'Brain Fog',
          pct: '65%',
          bg: 'from-[#9A7A9E] to-[#7A4A8E]',
          val: 'Lifting'
        }, {
          label: 'Skin Glow',
          pct: '55%',
          bg: 'from-[#E8C4B8] to-[#C07060]',
          val: 'Day 5 ✨'
        }].map((row, i) => <div key={i} className="flex justify-between items-center py-3 border-b border-black/5 last:border-0" data-id="element-360">
              <div className="text-sm text-text-mid w-[70px]" data-id="element-361">{row.label}</div>
              <div className="flex-1 mx-3.5 bg-cream rounded-full h-1.5 overflow-hidden" data-id="element-362">
                <div className={`h-full rounded-full bg-gradient-to-r ${row.bg}`} style={{
              width: row.pct
            }} data-id="element-363"></div>
              </div>
              <div className="text-[13px] font-bold text-text-dark min-w-[28px] text-right" data-id="element-364">
                {row.val}
              </div>
            </div>)}
        </div>
      </div>

      <div className="px-5 pb-5" data-id="element-365">
        <div className="text-base font-bold text-text-dark mb-2.5" data-id="element-366">
          📝 Today's Notes
        </div>
        <textarea className="w-full bg-white border border-black/5 rounded-2xl p-4 text-[15px] text-text-dark resize-none h-[100px] outline-none shadow-sm focus:border-sage-dark/50 transition-colors" placeholder="How are you feeling? Any changes you noticed today? Your jeans feeling looser?" data-id="element-367"></textarea>
        <button onClick={() => showToast("✅ Today's log saved!")} className="w-full bg-gradient-to-br from-sage-dark to-sage text-white border-none rounded-2xl p-4 text-base font-bold cursor-pointer mt-3 transition-transform active:scale-[0.98]" data-id="element-368">
          Save Check-In
        </button>
      </div>
    </div>;
}